import React, { useState, useEffect, useRef } from 'react';

const FOOD_PLACEHOLDER_IMG = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QBMRXhpZgAATU0AKgAAAAgABAEAAAQAAAABAAAEOAEBAAQAAAABAAAEQAESAAMAAAABAAEAAIdpAAQAAAABAAAAPgAAAAAAAAAAAAD/4gIoSUNDX1BST0ZJTEUAAQEAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANv/bAEMADQkKCwoIDQsKCw4ODQ8TIBUTEhITJxweFyAuKTEwLiktLDM6Sj4zNkY3LC1AV0FGTE5SU1IyPlphWlBgSlFST//bAEMBDg4OExETJhUVJk81LTVPT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT//AABEIAGQAYwMBIgACEQEDEQH/xAAZAAEBAQEBAQAAAAAAAAAAAAAAAQQDAgb/xAApEAEAAgIABQMEAgMAAAAAAAAAAQIDEQQSITFBIlFhEzJCcVLhgaHB/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAH/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwD7BREVRFAEUAAARQBFBNCgAICg90pvrbtreo769weB2vj5utdc2t6jtaPeHARQBQa+Hwa1e8dfEezLad2tMdpkR5UgFBAFEAV0xW/GZ1EzuJ/jLmCNMRMzrUxO+sR+M+8fBnw+j6nTmj7tdpMGesRrJ0mI1Fvh2tMZcd647RM6UYGrh8GvXeOviHnhsUTaZv3rP2+zTe9cdea0/wBg8cRk5MU6726QwQ95Mk5Lza3+I9ngFARQQBUVAURQGjBhyRX6lZ1PiJ8rw/D79d46eIOKzd8dJ/c/8VHTpmrGTHPLeOn9Sx35+eeffNHfb1hyTjtuO3mGvJjrnpFqz18SDCkLas1tNbRqYRFURQBAFAAdeGrW2WItr4/bkA28Rm5I5az6p/0xAqDrw+b6dtT9s9/hyRFbeKik4ubcb8T7sSoIKAoIAoAAAAAIoAIoAAAAIACgAIoAIAAAKAAAD//Z";

// ============================================================
// COMPONENTE: LazyImage
// Imagem com placeholder + IntersectionObserver, sem delay artificial.
// Se a imagem já estiver no cache do browser, mostra IMEDIATAMENTE
// (sem piscar/placeholder).
// ============================================================
const LazyImage = React.memo(({ src, alt, className, ...props }) => {
  // Detecta se já está no cache do browser ANTES do 1º paint — evita flash
  const imgIsCached = (() => {
    if (!src || typeof Image === 'undefined') return false;
    try {
      const probe = new Image();
      probe.src = src;
      return probe.complete && probe.naturalWidth > 0;
    } catch {
      return false;
    }
  })();

  const [showReal, setShowReal] = useState(imgIsCached);
  const [isInView, setIsInView] = useState(imgIsCached);
  const [currentSrc, setCurrentSrc] = useState(imgIsCached ? src : null);
  const containerRef = useRef(null);
  const prevSrcRef = useRef(src);
  
  const fallbackImage = "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=200&fit=crop&crop=center";
  
  // Reset se a URL mudar (mas só se for de fato uma URL diferente)
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      const probe = new Image();
      probe.src = src || '';
      if (probe.complete && probe.naturalWidth > 0) {
        setShowReal(true);
        setIsInView(true);
        setCurrentSrc(src);
      } else {
        setShowReal(false);
        setCurrentSrc(null);
      }
    }
  }, [src]);
  
  useEffect(() => {
    if (isInView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [isInView]);
  
  // Carregar imagem sem delay artificial (era 260ms — causava o "piscar")
  useEffect(() => {
    if (!isInView || !src || showReal) return;
    let cancelled = false;
    const img = new Image();
    img.src = src;
    
    const handleLoad = async () => {
      try {
        if (img.decode) {
          await img.decode();
        }
        if (cancelled) return;
        setCurrentSrc(src);
        setShowReal(true);
      } catch {
        if (!cancelled) {
          setCurrentSrc(fallbackImage);
          setShowReal(true);
        }
      }
    };
    
    const handleError = () => {
      if (!cancelled) {
        setCurrentSrc(fallbackImage);
        setShowReal(true);
      }
    };
    
    if (img.complete && img.naturalWidth > 0) {
      handleLoad();
    } else {
      img.onload = handleLoad;
      img.onerror = handleError;
    }
    
    return () => {
      cancelled = true;
    };
  }, [isInView, src, fallbackImage, showReal]);
  
  return (
    <div ref={containerRef} className={`relative overflow-hidden bg-gray-100 ${className}`} {...props}>
      <img 
        src={showReal ? currentSrc : FOOD_PLACEHOLDER_IMG}
        alt={showReal ? alt : ""}
        aria-hidden={!showReal}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
      />
    </div>
  );
});

export default LazyImage;
